import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, TextField,
  Paper, Alert, CircularProgress
} from '@mui/material';
// Change the import to use the UnifiedContainerSelector
import UnifiedContainerSelector from '../../tree/UnifiedContainerSelector';

/**
 * DecomposeDialog component - Shows AI-decomposed requirements and allows selection and editing
 * before creating them in the system
 * 
 * @component
 */
const DecomposeDialog = ({
  open,
  onClose,
  data,
  onCreateRequirements
}) => {
  const [selectedRows, setSelectedRows] = useState({});
  const [editedRequirements, setEditedRequirements] = useState([]);
  const [containerDialog, setContainerDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize data when it changes
  useEffect(() => {
    if (data?.requirements) {
      // Create a map of selected rows (default all to true)
      const selected = {};
      data.requirements.forEach((_, index) => {
        selected[index] = true;
      });
      setSelectedRows(selected);
      
      // Initialize edited requirements. `_key` is a React `key=` identity only,
      // stamped once here — the row set is fixed for this dialog's lifetime (no
      // add/remove UI; rows are only ever edited or selected in place, by index),
      // so a stable identity per row avoids inputs losing focus/value across
      // re-renders without needing the array index the rule flags.
      setEditedRequirements(data.requirements.map(req => ({ ...req, _key: uuidv4() })));
      setLoading(false);
    } else {
      setSelectedRows({});
      setEditedRequirements([]);
      setLoading(true);
    }
  }, [data]);

  // Toggle selection of a row
  const toggleRowSelection = (index) => {
    setSelectedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Toggle selection of all rows
  const toggleAllRows = () => {
    const allSelected = Object.values(selectedRows).every(Boolean);
    const newSelection = {};
    
    editedRequirements.forEach((_, index) => {
      newSelection[index] = !allSelected;
    });
    
    setSelectedRows(newSelection);
  };

  // Handle title change
  const handleTitleChange = (index, value) => {
    const updated = [...editedRequirements];
    updated[index] = {
      ...updated[index],
      title: value
    };
    setEditedRequirements(updated);
  };

  // Handle description change
  const handleDescriptionChange = (index, value) => {
    const updated = [...editedRequirements];
    updated[index] = {
      ...updated[index],
      description: value
    };
    setEditedRequirements(updated);
  };

  // Handle create requirements
  const handleCreate = () => {
    setContainerDialog(true);
  };

  // Handle container selection
  const handleContainerSelected = (containerId) => {
    // Get selected requirements
    const requirements = editedRequirements.filter((_, index) => selectedRows[index]);
    
    if (requirements.length === 0) {
      return;
    }
    
    // Call onCreateRequirements with selected requirements and container
    onCreateRequirements(requirements, containerId);
    setContainerDialog(false);
    onClose();
  };

  // Count selected rows
  const selectedCount = Object.values(selectedRows).filter(Boolean).length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: { sx: { height: '80vh', display: 'flex', flexDirection: 'column' } },
        }}
      >
        <DialogTitle>Decomposed Requirements</DialogTitle>
        
        <DialogContent dividers sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : !data?.requirements ? (
            <Alert severity="error">
              No requirements were generated. Please try again.
            </Alert>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2">
                  Select requirements to create ({selectedCount}/{editedRequirements.length})
                </Typography>
              </Box>
              
              <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedCount > 0 && selectedCount < editedRequirements.length}
                          checked={selectedCount === editedRequirements.length && selectedCount > 0}
                          onChange={toggleAllRows}
                        />
                      </TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedRequirements.map((req, index) => (
                      <TableRow key={req._key}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={!!selectedRows[index]}
                            onChange={() => toggleRowSelection(index)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={req.title}
                            onChange={(e) => handleTitleChange(index, e.target.value)}
                            disabled={!selectedRows[index]}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            variant="outlined"
                            size="small"
                            value={req.description}
                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                            disabled={!selectedRows[index]}
                            sx={{
                              '& .MuiInputBase-input': {
                                overflow: 'auto'
                              },
                              '& .MuiOutlinedInput-root': {
                                height: 'auto'
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            color="primary"
            disabled={loading || !data?.requirements || selectedCount === 0}
          >
            Create Selected ({selectedCount})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use ContainerSelector if UnifiedContainerSelector is not working */}
      {containerDialog && (
        <UnifiedContainerSelector
          open={containerDialog}
          onClose={() => setContainerDialog(false)}
          onSelect={handleContainerSelected}
          showCreateOption={true}
          title="Select Container for Decomposed Requirements"
          fallbackToLegacy={true} // Add this prop to fallback to legacy mode if needed
        />
      )}
    </>
  );
};

DecomposeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  onCreateRequirements: PropTypes.func.isRequired
};

export default DecomposeDialog;